import { Camera, Trash2 } from 'lucide-react'
import { useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { errorMessage } from '@/api/errors'
import { useUploadFile, type FileCategory } from '@/api/files'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import { tAny } from '@/i18n'

interface ImageUploadProps {
  name: string
  currentUrl: string | null
  category?: FileCategory
  onUploaded: (fileId: number | null) => void
  label?: string
  shape?: 'circle' | 'square'
}

/** Uploads an image to object storage immediately and reports the stored-file id to the form. */
export function ImageUpload({
  name,
  currentUrl,
  category = 'avatar',
  onUploaded,
  label = 'Photo',
  shape = 'circle',
}: ImageUploadProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const id = useId()
  const upload = useUploadFile()
  const [preview, setPreview] = useState<string | null>(currentUrl)
  const [error, setError] = useState<string | null>(null)

  const onFile = async (file: File | undefined) => {
    if (!file) return
    setError(null)
    try {
      const stored = await upload.mutateAsync({ file, category })
      setPreview(stored.url ?? URL.createObjectURL(file))
      onUploaded(stored.id)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <div className="flex items-center gap-4">
      <div className="relative">
        {shape === 'circle' ? (
          <Avatar name={name || '?'} src={preview} size="xl" className="ring-4 ring-surface-sunken" />
        ) : (
          <span className="grid size-20 place-items-center overflow-hidden rounded-card border border-line bg-surface-sunken">
            {preview ? (
              <img src={preview} alt="" className="size-full object-contain" />
            ) : (
              <Camera className="size-6 text-subtle" />
            )}
          </span>
        )}
        {upload.isPending ? (
          <span className="absolute inset-0 grid place-items-center rounded-full bg-surface/70">
            <Spinner className="size-5 text-brand-600" label={t('Uploading')} />
          </span>
        ) : null}
      </div>
      <div>
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={
            category === 'logo'
              ? 'image/png,image/jpeg,image/webp,image/svg+xml'
              : 'image/png,image/jpeg,image/webp'
          }
          className="sr-only"
          onChange={(e) => void onFile(e.target.files?.[0])}
        />
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Camera />}
            onClick={() => inputRef.current?.click()}
            loading={upload.isPending}
          >
            {preview
              ? tAny(`Change ${label.toLowerCase()}`, {
                  defaultValue: t('Change {{label}}', { label: label.toLowerCase() }),
                })
              : tAny(`Upload ${label.toLowerCase()}`, {
                  defaultValue: t('Upload {{label}}', { label: label.toLowerCase() }),
                })}
          </Button>
          {preview ? (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Trash2 />}
              onClick={() => {
                setPreview(null)
                onUploaded(null)
              }}
            >
              {t('Remove')}
            </Button>
          ) : null}
        </div>
        <p className="mt-1.5 text-xs text-muted">{t('JPG, PNG or WebP · max 2 MB')}</p>
        {error ? (
          <p className="mt-1 text-xs font-medium text-danger-600" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  )
}
